import Markdown from "react-markdown";
import ReactDOMServer from "react-dom/server";
import MarkdownIt from 'markdown-it';
import sanitizer from 'markdown-it-sanitizer';

// Initialize markdown-it with sanitizer plugin
const md = new MarkdownIt();
md.use(sanitizer);

/**
 * Sanitizes markdown content by parsing and re-rendering it
 * This helps normalize problematic characters
 */
export function sanitizeMarkdown(content) {
  // Parse and render back to markdown
  // This will handle special characters like < and >
  const rendered = md.render(content);
  
  // Convert HTML back to markdown (if needed)
  // For a simple approach, we can handle the most common HTML conversions
  return rendered
    .replace(/<p>(.*?)<\/p>/g, '$1\n\n')
    .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
    .replace(/<em>(.*?)<\/em>/g, '*$1*')
    .replace(/<code>(.*?)<\/code>/g, '`$1`')
    .replace(/&lt;/g, '\\<')
    .replace(/&gt;/g, '\\>')
    .replace(/&amp;/g, '&');
}

export const extractCommentsFromMarkdown = (markdown) => {
  const commentRegex = /<!--(.*?)-->/gs;
  const matches = [];
  let match;
  while ((match = commentRegex.exec(markdown)) !== null) {
    matches.push({
      raw: match[0],
      prompt: match[1].trim(),
      index: match.index,
    });
  }
  return matches;
};

export const findCodeBlocks = (langauge, markdown) => {
  const regex = /```(\w+)\n([\s\S]*?)```/g;
  let match;
  const codeBlocks = [];
  while ((match = regex.exec(markdown)) !== null) {
    if(match[1] === langauge) {
      codeBlocks.push({
        raw: match[0],
        prompt: match[2],
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    }
  }
  return codeBlocks;
};

export const findFirstCodeBlock = (langauge, markdown) => {
  const regex = /```(\w+)\n([\s\S]*?)```/g;
  let match;
  while ((match = regex.exec(markdown)) !== null) {
    if(match[1] === langauge) {
      return {
        raw: match[0],
        prompt: match[2],
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      };
    }
  }
  return null;
};

export const serializeCodeChildren = (children) => {
  if(!children) return "";
  if(Array.isArray(children)) {
    return children.map(serializeCodeChildren).join("");  
  }
  return children.props.children;
}

export const serializeChildren = (children, parent, index) => {
  if (!children) return "";

  if (typeof children === "string") {
    return children;
  }

  if(children.type === "em") {
    return `*${serializeChildren(children.props.children, children, 0)}*`;
  }

  if(children.type === "strong") {
    return `**${serializeChildren(children.props.children, children, 0)}**`;
  }

  if(children.type?.name === "code") {
    return `\`${serializeChildren(children.props.children, children, 0)}\``;
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => serializeChildren(child, children, index)).join(""); 
  }

  if(children.type === "li") {
    if(parent && parent.type === "ol") {
      return `${index + 1}. ${serializeChildren(children.props.children, children, 0)}`
    } else {
      return `- ${serializeChildren(children.props.children, children, 0)}`
    }
  }

  if(children.type === "a") {
    return `[${children.props.children}](${children.props.href})`
  }

};


export const copyRenderedMarkdownToClipboard = async ({
  markdown,
  successCallback = () => {},
  failCallback = () => {},
}) => {
  try {
    console.log(markdown);
    const htmlString = ReactDOMServer.renderToString(
      <Markdown>{markdown}</Markdown>
    );
    if (navigator.clipboard && window.ClipboardItem && window.Blob) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlString], { type: "text/html" }),
          "text/plain": new Blob([markdown], { type: "text/plain" }),
        }),
      ]);
      successCallback();
    } else {
      failCallback();
    }
  } catch (err) {
    console.error("Error copying to clipboard:", err);
  }
};